import { Signal, useComputed, useMonitor } from "@suse-o11y/signal";
import sortBy from "lodash/sortBy";
import uniq from "lodash/uniq";
import React from "react";

import { Button } from "~/components/button";
import { Truncated } from "~/components/truncated";
import { type NodeId, Type, type Version, type ViewSnapshot } from "~/models/dto";
import { getTime, toDate } from "~/tools/date-time";
import { usePermanent } from "~/tools/use-permanent";

import { TopologySnapshot } from "../../../../app/state/topology/TopologySnapshot";
import type {
	MonitorCheckStatus,
	MonitorCheckStatusComponent,
	MonitorCheckStatusRelatedFailuresCheckStatus,
} from "../../../../common/openapi";
import { Api } from "../../../../common/openapi";
import { ComponentLinkWithType } from "../../../../components/react/component-link/ComponentLinkWithType";
import { useClosestDrawer } from "../../../../components/react/drawer/use-closest-drawer";
import { GenericErrorMessage } from "../../../../components/react/error/GenericErrorMessage";
import { LinkTo } from "../../../../components/react/link";
import { LoadingSpinner } from "../../../../components/react/loading-spinner/LoadingSpinner";
import { Link } from "../../../../components/react/styled/StyledComponents";
import { queryTopologySnapshot } from "../../../../queries";
import { useQuery } from "../../../../queries/react-query";
import { COMPONENT_PATH, VIEW_MODE_TOPOLOGY } from "../../../../routes/Routes";
import { EXTENDED_PREVIEW_MODE } from "../../../../stackviz/StackVizModes";
import { StackViewSnapshot } from "../../../../store/StackViewSnapshot";

import { useMonitorStatusDrawerContext } from "./MonitorStatusDrawer/use-monitor-status-drawer-context";
import { RelatedHealthViolation } from "./RelatedHealthViolation";

interface MonitorRelatedIssuesGraphProps {
	currentCheckState: MonitorCheckStatus;
	data: ReadonlyArray<MonitorCheckStatusRelatedFailuresCheckStatus>;
	onMessageClick: (checkStateId: string) => void;
}

const stqlTemplate = (componentIds: Array<number>) => `id IN (${componentIds.map((id) => `"${id}"`).join(",")})`;

export const MonitorRelatedIssuesGraph: React.FC<MonitorRelatedIssuesGraphProps> = ({
	currentCheckState,
	data,
	onMessageClick,
}) => {
	const selectedComponentId = usePermanent(() => Signal<NodeId>());
	const sortedData = React.useMemo(() => {
		return sortBy(
			[
				...data,
				// Append the current health violation to include it in the table and topology
				{
					id: currentCheckState.id,
					identifier: currentCheckState.identifier,
					health: currentCheckState.health,
					name: currentCheckState.message,
					component: currentCheckState.component,
					topology: { distance: 0, relation: Api.MonitorCheckStatusRelatedFailuresTopology.RelationEnum.SELF },
					triggeredTimestamp: currentCheckState.triggeredTimestamp,
				},
			],
			["topology.distance", "name"],
		);
	}, [data, currentCheckState]);

	const query = stqlTemplate(uniq(sortedData.map(({ component }) => component.id)));

	const dataWithSelectedComponent = useComputed(
		(monitor) => sortedData.filter(({ component }) => component.id === selectedComponentId.read(monitor)),
		[sortedData, selectedComponentId],
	);

	const selectedData = dataWithSelectedComponent.length > 0 ? dataWithSelectedComponent : sortedData;

	return (
		<div className="flex max-h-[65vh] min-h-[500px] flex-wrap" data-testid="monitor-related-issues-graph">
			<div className="w-full xs:w-2/3 py-2 pr-6 pl-2 max-xs:h-[40vh]">
				<TopologyPreviewGraph
					currentComponentIdentifier={currentCheckState.component.identifier}
					query={query}
					selectedComponentId={selectedComponentId}
				/>
			</div>
			<div className="flex w-full xs:w-1/3 flex-col border-0 border-l border-l-grey-300 border-solid py-4 pl-6 max-xs:h-[25vh]">
				{dataWithSelectedComponent.length === 0 ? (
					<h4 className="mb-2 leading-5">All related health violations</h4>
				) : (
					<RelatedHealthViolationTitle
						component={dataWithSelectedComponent[0].component}
						currentComponentId={currentCheckState.component.id} // all items are from same component, so it's fine to extract component from first item
						onViewAllClick={() => selectedComponentId.write(undefined)}
					/>
				)}
				<div className="flex flex-col overflow-y-auto">
					{selectedData.map((item) => (
						<RelatedHealthViolation
							className="py-2"
							item={item}
							key={item.identifier}
							onMessageClick={() => onMessageClick(item.identifier)}
							showAsLink={item.identifier !== currentCheckState.identifier}
							showTriggeredTimestamp={false}
						>
							{/*Avoid redundant info when we have filtered a single component to inspect*/}
							{dataWithSelectedComponent.length === 0 && (
								<>
									on
									<RelatedHealthViolationSource
										component={item.component}
										currentComponentId={currentCheckState.component.id}
									/>
								</>
							)}
						</RelatedHealthViolation>
					))}
				</div>
			</div>
		</div>
	);
};

const RelatedHealthViolationSource: React.FC<{
	currentComponentId: number;
	component: MonitorCheckStatusComponent;
}> = ({ currentComponentId, component }) => {
	const monitor = useMonitor();
	const { timestamp, timeRange } = useMonitorStatusDrawerContext();
	const drawer = useClosestDrawer();

	if (currentComponentId === component.id) {
		return <Truncated className="italic">current component</Truncated>;
	}

	return (
		<ComponentLinkWithType
			identifier={component.identifier}
			name={component.name}
			onClick={() => drawer?.close()}
			timeRange={timeRange.read(monitor)}
			timestamp={timestamp.read(monitor)}
			type={component.type}
		/>
	);
};

const RelatedHealthViolationTitle: React.FC<{
	currentComponentId: number;
	component: MonitorCheckStatusComponent;
	onViewAllClick: VoidFunction;
}> = ({ currentComponentId, component, onViewAllClick }) => (
	<div className="flex justify-between gap-2">
		<h4 className="mb-2 flex min-w-0 gap-1 whitespace-nowrap leading-5">
			Related health violations on
			<RelatedHealthViolationSource component={component} currentComponentId={currentComponentId} />
		</h4>
		<Link className="whitespace-nowrap font-normal text-13" onClick={onViewAllClick}>
			View all
		</Link>
	</div>
);

const LazyStackViz = React.lazy(() =>
	import("../../../../stackviz/StackViz").then(({ StackViz }) => ({ default: StackViz })),
);

function selectStackViewSnapshot(snapshot: ViewSnapshot) {
	return StackViewSnapshot.fromTopologySnapshot(TopologySnapshot.fromViewSnapshot(snapshot));
}

interface TopologyGraphProps {
	query: string;
	currentComponentIdentifier: string;
	selectedComponentId: Signal<undefined | NodeId>;
}

const TopologyPreviewGraph: React.FC<TopologyGraphProps> = ({
	query,
	currentComponentIdentifier,
	selectedComponentId,
}) => {
	const monitor = useMonitor();
	const { timestamp, timeRange } = useMonitorStatusDrawerContext();

	const { data: stackViewSnapshot, error } = useQuery({
		select: selectStackViewSnapshot,

		...queryTopologySnapshot({
			_type: Type.ViewSnapshotRequest,
			query,
			queryVersion: "0.0.1" as Version,
			metadata: {
				_type: Type.QueryMetadata,
				groupingEnabled: false,
				showIndirectRelations: true,
				minGroupSize: 0,
				groupedByLayer: false,
				groupedByDomain: false,
				groupedByRelation: false,
				queryTime: getTime(timestamp.read(monitor)),
				autoGrouping: false,
				connectedComponents: false,
				neighboringComponents: false,
				showFullComponent: true,
			},
		}),
	});

	if (error) {
		return <GenericErrorMessage className="h-full" error={error} />;
	}

	if (stackViewSnapshot == null) {
		return <LoadingSpinner className="h-full" size="large" />;
	}

	if (stackViewSnapshot.numberOfVisibleElements() === 0) {
		return <GenericErrorMessage className="h-full" error={undefined} />;
	}

	return (
		<div className="relative h-full w-full">
			<React.Suspense fallback={<LoadingSpinner className="size-full" size="large" />}>
				<LinkTo
					className="absolute right-0 z-10"
					params={{
						identifier: currentComponentIdentifier,
						mode: VIEW_MODE_TOPOLOGY,
					}}
					search={(search) => ({
						...search,
						query: undefined,
						explore: {
							type: "QUERY",
							query,
						},
						view: {
							groupedByDomains: false,
							groupedByLayers: false,
							groupedByRelations: false,
							showIndirectRelations: true,
							groupingEnabled: false,
							tags: [],
							eventFilters: { _type: Type.ViewEventFilters, types: [], tags: [], categories: [], sources: [] },
							autoGrouping: false,
							connectedComponents: false,
							neighboringComponents: false,
						},
						timestamp: toDate(timestamp.read(monitor)),
						timeRange: timeRange.read(monitor),
					})}
					to={COMPONENT_PATH}
				>
					<Button variant="secondary">Explore topology</Button>
				</LinkTo>
				<LazyStackViz
					enableZoomWithKey={true}
					mode={EXTENDED_PREVIEW_MODE}
					selectedComponentId={selectedComponentId}
					spotlightedComponentIdentifier={currentComponentIdentifier}
					stackViewSnapshot={stackViewSnapshot}
				/>
			</React.Suspense>
		</div>
	);
};
